import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SystemHealthMetrics {
  isHealthy: boolean;
  dbConnected: boolean;
  responseTime: number;
  errorCount: number;
  lastCheck: Date;
  issues: string[];
  healthScore: number;
  criticalIssues: string[];
  warnings: string[];
  systemLoad: {
    tasksCount: number;
    notificationsCount: number;
    unreadNotifications: number;
    overdueTasksCount: number;
    employeesCount: number;
    clientsCount: number;
  };
  recommendations: string[];
}

export const useEnhancedSystemHealth = () => {
  const [health, setHealth] = useState<SystemHealthMetrics>({
    isHealthy: true,
    dbConnected: true,
    responseTime: 0,
    errorCount: 0,
    lastCheck: new Date(),
    issues: [],
    healthScore: 100,
    criticalIssues: [],
    warnings: [],
    systemLoad: {
      tasksCount: 0,
      notificationsCount: 0,
      unreadNotifications: 0,
      overdueTasksCount: 0,
      employeesCount: 0,
      clientsCount: 0,
    },
    recommendations: [],
  });

  const checkSystemHealth = async () => {
    const startTime = Date.now();
    const issues: string[] = [];
    const criticalIssues: string[] = [];
    const warnings: string[] = [];
    const recommendations: string[] = [];
    
    try {
      // فحص الاتصال بقاعدة البيانات والحصول على البيانات
      const [employeesResult, notificationsResult, tasksResult, clientsResult] = await Promise.allSettled([
        supabase.from('employees').select('id', { count: 'exact', head: true }),
        supabase.from('notifications').select('id, is_read', { count: 'exact' }),
        supabase.from('tasks').select('id, status, due_date', { count: 'exact' }),
        supabase.from('clients').select('id', { count: 'exact', head: true })
      ]);
      
      const responseTime = Date.now() - startTime;
      let dbConnected = true;
      
      // تحليل نتائج الاستعلامات
      const employeesCount = employeesResult.status === 'fulfilled' ? (employeesResult.value.count || 0) : 0;
      const clientsCount = clientsResult.status === 'fulfilled' ? (clientsResult.value.count || 0) : 0;
      
      let notificationsCount = 0;
      let unreadNotifications = 0;
      if (notificationsResult.status === 'fulfilled') {
        notificationsCount = notificationsResult.value.count || 0;
        unreadNotifications = notificationsResult.value.data?.filter(n => !n.is_read).length || 0;
      }
      
      let tasksCount = 0;
      let overdueTasksCount = 0;
      if (tasksResult.status === 'fulfilled') {
        tasksCount = tasksResult.value.count || 0;
        const now = new Date().toISOString();
        overdueTasksCount = tasksResult.value.data?.filter(t => 
          t.due_date && t.due_date < now && !['completed', 'cancelled'].includes(t.status)
        ).length || 0;
      }
      
      // فحص الأخطاء
      const failedQueries = [employeesResult, notificationsResult, tasksResult, clientsResult]
        .filter(result => result.status === 'rejected');
      
      if (failedQueries.length > 0) {
        dbConnected = false;
        criticalIssues.push(`فشل ${failedQueries.length} من استعلامات قاعدة البيانات`);
      }
      
      // تقييم صحة النظام
      let healthScore = 100;
      
      // تقييم سرعة الاستجابة
      if (responseTime > 3000) {
        criticalIssues.push(`بطء شديد في الاستجابة: ${responseTime}ms`);
        healthScore -= 40;
        recommendations.push('فحص اتصال الشبكة وتحسين استعلامات قاعدة البيانات');
      } else if (responseTime > 1000) {
        warnings.push(`بطء في الاستجابة: ${responseTime}ms`);
        healthScore -= 15;
        recommendations.push('مراقبة أداء قاعدة البيانات');
      }
      
      // تقييم المهام المتأخرة
      if (overdueTasksCount > 0) {
        if (overdueTasksCount > 10) {
          criticalIssues.push(`عدد كبير من المهام المتأخرة: ${overdueTasksCount}`);
          healthScore -= 25;
          recommendations.push('مراجعة عاجلة للمهام المتأخرة وإعادة تنظيمها');
        } else if (overdueTasksCount > 5) {
          warnings.push(`مهام متأخرة تحتاج متابعة: ${overdueTasksCount}`);
          healthScore -= 10;
          recommendations.push('متابعة المهام المتأخرة مع الفرق المسؤولة');
        } else {
          recommendations.push('مراقبة المهام لتجنب التأخير');
        }
      }
      
      // تقييم الإشعارات
      if (unreadNotifications > 50) {
        warnings.push(`عدد كبير من الإشعارات غير المقروءة: ${unreadNotifications}`);
        healthScore -= 10;
        recommendations.push('مراجعة نظام الإشعارات وتحسين كفاءته');
      } else if (unreadNotifications > 20) {
        warnings.push(`إشعارات غير مقروءة تحتاج متابعة: ${unreadNotifications}`);
        healthScore -= 5;
      }
      
      // تقييم البيانات الأساسية
      if (employeesCount === 0) {
        criticalIssues.push('لا توجد بيانات موظفين في النظام');
        healthScore -= 30;
        recommendations.push('إضافة بيانات الموظفين الأساسية');
      }
      
      if (clientsCount === 0) {
        warnings.push('لا توجد بيانات عملاء في النظام');
        healthScore -= 10;
        recommendations.push('بدء إدخال بيانات العملاء');
      }
      
      // إضافة تحديثات إيجابية
      if (healthScore >= 90) {
        recommendations.push('النظام يعمل بكفاءة عالية - استمر في المراقبة');
      } else if (healthScore >= 70) {
        recommendations.push('النظام يعمل بشكل جيد مع بعض التحسينات المطلوبة');
      }
      
      // إضافة التحذيرات والمشاكل الحرجة للقائمة العامة
      issues.push(...criticalIssues, ...warnings);
      
      setHealth({
        isHealthy: criticalIssues.length === 0 && healthScore >= 70,
        dbConnected,
        responseTime,
        errorCount: criticalIssues.length,
        lastCheck: new Date(),
        issues,
        healthScore: Math.max(0, healthScore),
        criticalIssues,
        warnings,
        systemLoad: {
          tasksCount,
          notificationsCount,
          unreadNotifications,
          overdueTasksCount,
          employeesCount,
          clientsCount,
        },
        recommendations,
      });
      
    } catch (error) {
      console.error('System health check failed:', error);
      setHealth({
        isHealthy: false,
        dbConnected: false,
        responseTime: Date.now() - startTime,
        errorCount: 1,
        lastCheck: new Date(),
        issues: [`فشل فحص النظام: ${error}`],
        healthScore: 0,
        criticalIssues: [`فشل فحص النظام: ${error}`],
        warnings: [],
        systemLoad: {
          tasksCount: 0,
          notificationsCount: 0,
          unreadNotifications: 0,
          overdueTasksCount: 0,
          employeesCount: 0,
          clientsCount: 0,
        },
        recommendations: ['إعادة تشغيل النظام والتحقق من الاتصال'],
      });
    }
  };

  useEffect(() => {
    // فحص فوري عند التحميل
    checkSystemHealth();
    
    // فحص دوري كل 60 ثانية
    const interval = setInterval(checkSystemHealth, 60000);
    
    return () => clearInterval(interval);
  }, []);

  return { health, checkSystemHealth };
};